<template>
  <header class="header">
    <div class="container flex-between">
      <div class="logo">
        <router-link to="/">
          <img src="/src/assets/images/logo.svg" alt="RC INOVAÇÕES" />
        </router-link>
      </div>
      
      <div class="mobile-menu" @click="toggleMenu" :class="{ 'active': isMenuOpen }">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <nav class="nav" :class="{ 'active': isMenuOpen }">
        <ul class="flex">
          <li><router-link to="/" @click="closeMenu">{{ $t('nav.home') }}</router-link></li>
          <li><router-link to="/about" @click="closeMenu">{{ $t('nav.about') }}</router-link></li>
          <li><router-link to="/services" @click="closeMenu">{{ $t('nav.services') }}</router-link></li>
          <li><router-link to="/projects" @click="closeMenu">{{ $t('nav.projects') }}</router-link></li>
          <li><router-link to="/contact" @click="closeMenu">{{ $t('nav.contact') }}</router-link></li>
          <li class="language-dropdown">
            <div class="selected-language" @click="toggleLanguageMenu">
              <img :src="getLanguageFlag(selectedLocale)" :alt="selectedLocale" />
              <span>{{ getLanguageName(selectedLocale) }}</span>
              <span class="arrow">▼</span>
            </div>
            <div class="language-menu" v-if="isLanguageMenuOpen">
              <div 
                v-for="locale in availableLocales" 
                :key="locale" 
                class="language-option"
                @click="changeLanguage(locale)"
                :class="{ 'active': selectedLocale === locale }"
              >
                <img :src="getLanguageFlag(locale)" :alt="locale" />
                <span>{{ getLanguageName(locale) }}</span>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { locale, availableLocales } = useI18n();
const selectedLocale = computed(() => locale.value);
const isMenuOpen = ref(false);
const isLanguageMenuOpen = ref(false);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  if (isMenuOpen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'auto';
  }
};

const closeMenu = () => {
  isMenuOpen.value = false;
  document.body.style.overflow = 'auto';
};

const toggleLanguageMenu = (event) => {
  event.stopPropagation();
  isLanguageMenuOpen.value = !isLanguageMenuOpen.value;
};

const changeLanguage = (newLocale) => {
  locale.value = newLocale;
  isLanguageMenuOpen.value = false;
  localStorage.setItem('userLocale', newLocale);
};

const getLanguageFlag = (locale) => {
  const flags = {
    'pt-BR': new URL('../assets/images/flags/brazil.svg', import.meta.url).href,
    'pt-PT': new URL('../assets/images/flags/portugal.svg', import.meta.url).href,
    'en-US': new URL('../assets/images/flags/usa.svg', import.meta.url).href,
    'en-GB': new URL('../assets/images/flags/uk.svg', import.meta.url).href,
    'es': new URL('../assets/images/flags/spain.svg', import.meta.url).href,
    'it': new URL('../assets/images/flags/italy.svg', import.meta.url).href
  };
  return flags[locale];
};

const getLanguageName = (locale) => {
  const names = {
    'pt-BR': 'Português (BR)',
    'pt-PT': 'Português (PT)',
    'en-US': 'English (US)',
    'en-GB': 'English (UK)',
    'es': 'Español',
    'it': 'Italiano'
  };
  return names[locale];
};

const handleClickOutside = (event) => {
  if (isLanguageMenuOpen.value && !event.target.closest('.language-dropdown')) {
    isLanguageMenuOpen.value = false;
  }
};

onMounted(() => {
  const savedLocale = localStorage.getItem('userLocale');
  if (savedLocale && availableLocales.includes(savedLocale)) {
    locale.value = savedLocale;
  }
  
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 15px 0;
  font-family: 'Roboto', sans-serif;
}

.logo img {
  height: 40px;
  width: auto;
}

.nav ul {
  list-style: none;
  gap: 20px;
}

.nav a {
  font-weight: 500;
  padding: 8px 0;
  position: relative;
  font-family: 'Roboto', sans-serif;
}

.nav a:after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.nav a:hover:after,
.nav a.router-link-active:after {
  width: 100%;
}

.mobile-menu {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 20px;
  cursor: pointer;
}

.mobile-menu span {
  height: 3px;
  width: 100%;
  background-color: var(--primary-color);
  border-radius: 3px;
  transition: all 0.3s ease;
}

.language-dropdown {
  position: relative;
  margin-left: 20px;
}

.selected-language {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: var(--border-radius);
  background-color: var(--light-background);
  transition: background-color 0.3s ease;
  font-family: 'Roboto', sans-serif;
}

.selected-language:hover {
  background-color: #e7f0f7;
}

.selected-language img {
  width: 20px;
  height: 15px;
  margin-right: 8px;
  object-fit: cover;
}

.arrow {
  margin-left: 8px;
  font-size: 10px;
}

.language-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 5px;
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  z-index: 10;
  width: 180px;
}

.language-option {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-family: 'Roboto', sans-serif;
}

.language-option.active {
  background-color: var(--light-background);
  font-weight: 500;
}

.language-option:hover {
  background-color: var(--light-background);
}

.language-option img {
  width: 20px;
  height: 15px;
  margin-right: 10px;
  object-fit: cover;
}

@media (max-width: 992px) {
  .mobile-menu {
    display: flex;
    z-index: 1001;
  }
  
  .mobile-menu.active span:first-child {
    transform: translateY(8px) rotate(45deg);
  }
  
  .mobile-menu.active span:nth-child(2) {
    opacity: 0;
  }
  
  .mobile-menu.active span:last-child {
    transform: translateY(-8px) rotate(-45deg);
  }
  
  .nav {
    position: fixed;
    top: 0;
    right: -100%;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background-color: white;
    box-shadow: -3px 0 10px rgba(0, 0, 0, 0.1);
    padding-top: 80px;
    transition: right 0.3s ease;
    overflow-y: auto;
  }
  
  .nav.active {
    right: 0;
  }
  
  .nav ul {
    flex-direction: column;
    gap: 0;
    padding: 0 20px;
  }
  
  .nav a {
    display: block;
    padding: 15px 0;
    border-bottom: 1px solid var(--border-color);
  }
  
  .language-dropdown {
    margin: 15px 0 0;
  }
  
  .language-menu {
    position: static;
    box-shadow: none;
    border: 1px solid var(--border-color);
    margin-top: 5px;
    width: 100%;
  }
}
</style> 